import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Save, UserPlus, Mail, Lock as LockIcon, Building2, Shield, User as UserIcon,
  CheckCircle, XCircle, Loader, Eye, EyeOff, AlertCircle
} from 'lucide-react';


import { useAuth } from '../../context/AuthContext';
import backendApi from '../../services/backendApi';

import {
  debounce,
  validateEmail,
  validateUsername,
  calculatePasswordStrength,
  getFieldStatusClass
} from '../../utils/validation';
import {
  ModuleCatalogItem,
  loadModuleCatalog,
  getModuleSelectionMap,
  applyUserOverrides,
} from '../../utils/moduleAccessAdmin';

interface ValidationState {
  isValid: boolean;
  message: string;
  isChecking: boolean;
}

const UserCreate: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [sites, setSites] = useState<any[]>([]);
  const [roles, setRoles] = useState<any[]>([]);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [companySubscription, setCompanySubscription] = useState<any>({
    max_sites: null,
    max_users: null,
  });
  const [showLimitAlert, setShowLimitAlert] = useState(false);
  const [limitMessage, setLimitMessage] = useState('');

  const [formData, setFormData] = useState({
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    password: '',
    confirm_password: '',
    role: '', // Initialize empty, user must select
    site_id: '',
    is_active: true,
    country_code: '',
    phone_number: '',
    company_id: ''
  });

  const { user } = useAuth();
  const isSystemLevelAdmin =
    !!user?.isGlobalAdmin ||
    user?.role === 'system_admin' ||
    user?.role === 'super_admin' ||
    user?.role === 'admin';

  // Detailed role detection for filtering creatable roles
  const currentRoleName = String(user?.roleName || user?.role || '').toLowerCase();
  const isHoAdmin = currentRoleName === 'ho_admin' || currentRoleName === 'company_admin' || (currentRoleName === 'admin' && !user?.siteCode);
  const isSiteAdmin = currentRoleName === 'site_admin' || (currentRoleName === 'admin' && !!user?.siteCode);
  const [companies, setCompanies] = useState<any[]>([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [apiError, setApiError] = useState<string | null>(null);
  const [moduleCatalog, setModuleCatalog] = useState<ModuleCatalogItem[]>([]);
  const [companyAllowedModuleKeys, setCompanyAllowedModuleKeys] = useState<string[]>([]);
  const [siteAllowedModuleKeys, setSiteAllowedModuleKeys] = useState<string[]>([]);
  const [userModuleSelection, setUserModuleSelection] = useState<Record<string, boolean>>({});
  const [applyCustomUserModuleAccess, setApplyCustomUserModuleAccess] = useState(false);

  // Real-time validation states
  const [usernameValidation, setUsernameValidation] = useState<ValidationState>({
    isValid: false,
    message: '',
    isChecking: false
  });

  const [emailValidation, setEmailValidation] = useState<ValidationState>({
    isValid: false,
    message: '',
    isChecking: false
  });

  const [passwordStrength, setPasswordStrength] = useState<{
    strength: number;
    label: string;
    feedback: string[];
    isValid: boolean;
  }>({
    strength: 0,
    label: '',
    feedback: [],
    isValid: false
  });

  // Refs for debounced functions
  const usernameCheckRef = useRef<NodeJS.Timeout | null>(null);
  const emailCheckRef = useRef<NodeJS.Timeout | null>(null);

  const normalizedRole = String(formData.role || '').toLowerCase();
  const roleRequiresSite = ['site_admin', 'general_user', 'user', 'admin'].includes(normalizedRole);
  const roleRequiresCompany = !['system_admin', 'super_admin'].includes(normalizedRole);
  const isHoScopedRole = ['ho_admin', 'company_admin'].includes(normalizedRole);
  const isSiteScopedRole = normalizedRole === 'site_admin';
  const isGeneralUserRole = normalizedRole === 'general_user';

  const availableModuleKeys = isHoScopedRole
    ? companyAllowedModuleKeys
    : roleRequiresSite
      ? siteAllowedModuleKeys
      : companyAllowedModuleKeys;

  const availableUserModules = moduleCatalog.filter(
    (module) => availableModuleKeys.includes(module.module_key)
  );

  const syncUserSelectionToAllowedKeys = (allowedKeys: string[]) => {
    setUserModuleSelection((prev) => {
      const next: Record<string, boolean> = {};
      moduleCatalog.forEach((module) => {
        if (allowedKeys.includes(module.module_key)) {
          next[module.module_key] = prev[module.module_key] !== undefined ? prev[module.module_key] : true;
        }
      });
      return next;
    });
  };

  useEffect(() => {
    fetchRoles();
    fetchCompanies();
    if (isSystemLevelAdmin) {
      fetchSites();
    } else {
      // Pre-fill company if not system admin
      if (user?.companyId) { // Fixed property name
        const companyId = user.companyId!.toString();
        setFormData(prev => ({ ...prev, company_id: companyId }));
        fetchSites(companyId);
      } else {
        fetchSites();
      }
    }

    return () => {
      // Cleanup debounced timeouts
      if (usernameCheckRef.current) clearTimeout(usernameCheckRef.current);
      if (emailCheckRef.current) clearTimeout(emailCheckRef.current);
    };
  }, [user, isSystemLevelAdmin]);

  useEffect(() => {
    const loadMeta = async () => {
      try {
        const modules = await loadModuleCatalog(backendApi);
        setModuleCatalog(modules);
        setUserModuleSelection(getModuleSelectionMap(modules));
      } catch (error) {
        console.error('Failed to load user module metadata:', error);
      }
    };

    loadMeta();
  }, []);

  const loadCompanyModuleAccess = async (companyId: string | null) => {
    if (!companyId) {
      setCompanyAllowedModuleKeys([]);
      return;
    }

    try {
      const response = await backendApi.getNavigationSubscriptions(companyId);
      if (!response.success) {
        setCompanyAllowedModuleKeys([]);
        return;
      }

      const activeKeys: string[] = Array.from(new Set<string>(
        (response.subscriptions || [])
          .filter((item: any) => String(item?.status || '').toLowerCase() === 'active')
          .map((item: any) => String(item?.module_key || '').trim())
          .filter((key: string) => key.length > 0)
      ));

      setCompanyAllowedModuleKeys(activeKeys);
    } catch (error) {
      console.error('Failed to load company module subscriptions:', error);
      setCompanyAllowedModuleKeys([]);
    }
  };

  const loadSiteModuleAccess = async (siteId: string | null, companyId: string | null, roleNameForPolicy: string) => {
    if (!siteId || !companyId) {
      setSiteAllowedModuleKeys([]);
      return;
    }

    try {
      const response = await backendApi.getNavigationRolePolicies({
        companyId,
        siteId,
        scopeLevel: 'site',
        roleName: roleNameForPolicy,
      });

      if (response.success && Array.isArray(response.allowed_module_keys) && response.allowed_module_keys.length > 0) {
        setSiteAllowedModuleKeys(response.allowed_module_keys);
      } else {
        // Fallback to company module set if explicit site policies are not found yet.
        setSiteAllowedModuleKeys(companyAllowedModuleKeys);
      }
    } catch (error) {
      console.error('Failed to load site role policies:', error);
      setSiteAllowedModuleKeys(companyAllowedModuleKeys);
    }
  };

  useEffect(() => {
    const allowed = availableModuleKeys;
    syncUserSelectionToAllowedKeys(allowed);
  }, [moduleCatalog, companyAllowedModuleKeys, siteAllowedModuleKeys, normalizedRole]);

  const fetchRoles = async () => {
    try {
      const response = await backendApi.getRoles();
      if (response.success && response.roles) {
        let availableRoles = response.roles;

        // Filter roles based on current user's role
        // Site Admin: can only create general_user
        // HO Admin: can create ho_admin, site_admin, general_user
        // System Admin: can create all roles
        if (isSiteAdmin) {
          availableRoles = availableRoles.filter((r: any) =>
            ['general_user'].includes((r.role_name || '').toLowerCase())
          );
        } else if (isHoAdmin) {
          availableRoles = availableRoles.filter((r: any) =>
            ['ho_admin', 'site_admin', 'general_user'].includes((r.role_name || '').toLowerCase())
          );
        }
        // System admin sees all roles (no filter)

        setRoles(availableRoles);
        // Set default role if available
        if (availableRoles.length > 0) {
          // Check if 'general_user' exists and set as default, else first one
          const defaultRole = availableRoles.find((r: any) => r.role_name === 'general_user') || availableRoles[0];
          setFormData(prev => ({ ...prev, role: defaultRole.role_name }));
        }
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  const fetchCompanySubscription = async (companyId: string) => {
    try {
      const companiesResponse = await backendApi.getCompanies();
      if (companiesResponse.success && companiesResponse.companies) {
        const company = companiesResponse.companies.find(
          (c: any) => String(c.company_id) === String(companyId)
        );
        if (company) {
          setCompanySubscription({
            max_sites: company.max_sites || 10,
            max_users: company.max_users || 10,
          });
        }
      }
    } catch (error) {
      console.error('Failed to fetch company subscription:', error);
    }
  };

  const fetchCompanies = async () => {
    setLoadingCompanies(true);
    try {
      const response = await backendApi.getCompanies();
      if (response.success && response.companies && response.companies.length > 0) {
        let unique = Array.from(new Map(response.companies.map((item: any) => [item.company_id, item])).values()) as any[];

        // If user has a companyId but it's not in the fetched list, add a fallback entry
        // ONLY if the user is NOT a system admin (System Admins should see only onboarded customer companies)
        if (!isSystemLevelAdmin && user?.companyId && !unique.find((c: any) => String(c.company_id) === String(user.companyId))) {
          unique.unshift({
            company_id: user.companyId,
            company_name: user.company || 'Assigned Company',
            company_code: String(user.companyId)
          });
        }
        setCompanies(unique);
        const selectedCompany = String(formData.company_id || (!isSystemLevelAdmin && user?.companyId ? user.companyId : ''));
        if (selectedCompany) {
          void loadCompanyModuleAccess(selectedCompany);
        }
      } else if (!isSystemLevelAdmin && user?.companyId) {
        // API returned empty or failed — use fallback from user context for non-system admins
        setCompanies([{
          company_id: user.companyId,
          company_name: user.company || 'Assigned Company',
          company_code: String(user.companyId)
        }]);
        void loadCompanyModuleAccess(String(user.companyId));
        void fetchCompanySubscription(String(user.companyId));
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
      if (!isSystemLevelAdmin && user?.companyId) {
        setCompanies([{
          company_id: user.companyId,
          company_name: user.company || 'Assigned Company',
          company_code: String(user.companyId)
        }]);
        void loadCompanyModuleAccess(String(user.companyId));
      }
    } finally {
      setLoadingCompanies(false);
    }
  };

  const fetchSites = async (companyId: string | null = null) => {
    try {
      const response = await backendApi.getSites(false, '', companyId);
      if (response.success && response.sites) {
        const unique = Array.from(new Map(response.sites.map((item: any) => [item.site_id, item])).values());
        setSites(unique);
      }
    } catch (error) {
      console.error('Error fetching sites:', error);
    }
  };

  const filteredSites = formData.company_id
    ? sites.filter(s => Number(s.company_id) === Number(formData.company_id))
    : sites;


  // Debounced username availability check
  const checkUsernameAvailability = useCallback(
    debounce(async (username: string, siteId: string | null) => {
      if (!username || username.length < 3) {
        setUsernameValidation({ isValid: false, message: '', isChecking: false });
        return;
      }

      const formatCheck = validateUsername(username);
      if (!formatCheck.valid) {
        setUsernameValidation({
          isValid: false,
          message: formatCheck.message,
          isChecking: false
        });
        return;
      }

      setUsernameValidation(prev => ({ ...prev, isChecking: true }));

      try {
        // Handle siteId: pass string when available, null otherwise to match backendApi signature
        const siteIdParam = siteId ? siteId : null;
        const result = await backendApi.checkUsernameAvailability(
          username,
          siteIdParam as any,
          undefined,
          undefined
        );

        if (result.success) {
          setUsernameValidation({
            isValid: result.available,
            message: result.message,
            isChecking: false
          });
        } else {
          setUsernameValidation({
            isValid: false,
            message: result.error || 'Could not check username availability',
            isChecking: false
          });
        }
      } catch (error: any) {
        setUsernameValidation({
          isValid: false,
          message: 'Error checking username availability',
          isChecking: false
        });
      }
    }, 600),
    []
  );

  // Debounced email validation and check
  const checkEmail = useCallback(
    debounce(async (email: string) => {
      if (!email) {
        setEmailValidation({ isValid: false, message: '', isChecking: false });
        return;
      }

      const formatCheck = validateEmail(email);
      if (!formatCheck.valid) {
        setEmailValidation({
          isValid: false,
          message: formatCheck.message,
          isChecking: false
        });
        return;
      }

      // Check if email exists by attempting to use it (we'll check during submission)
      setEmailValidation({
        isValid: true,
        message: 'Email format is valid',
        isChecking: false
      });
    }, 500),
    []
  );

  // Handle username change
  const handleUsernameChange = (value: string) => {
    setFormData({ ...formData, username: value });
    setErrors({ ...errors, username: '' });

    if (value) {
      checkUsernameAvailability(value, formData.site_id || null);
    } else {
      setUsernameValidation({ isValid: false, message: '', isChecking: false });
    }
  };

  // Handle email change
  const handleEmailChange = (value: string) => {
    setFormData({ ...formData, email: value });
    setErrors({ ...errors, email: '' });

    if (value) {
      checkEmail(value);
    } else {
      setEmailValidation({ isValid: false, message: '', isChecking: false });
    }
  };

  // Handle password change
  const handlePasswordChange = (value: string) => {
    setFormData({ ...formData, password: value });
    setErrors({ ...errors, password: '' });

    const strength = calculatePasswordStrength(value);
    setPasswordStrength(strength);

    // Check password match in real-time
    if (formData.confirm_password) {
      if (value !== formData.confirm_password) {
        setErrors(prev => ({
          ...prev,
          confirm_password: 'Passwords do not match'
        }));
      } else {
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.confirm_password;
          return newErrors;
        });
      }
    }
  };

  // Handle confirm password change
  const handleConfirmPasswordChange = (value: string) => {
    setFormData({ ...formData, confirm_password: value });

    if (formData.password) {
      if (value !== formData.password) {
        setErrors(prev => ({
          ...prev,
          confirm_password: 'Passwords do not match'
        }));
      } else {
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.confirm_password;
          return newErrors;
        });
      }
    }
  };

  // Handle site change - recheck username if needed
  const handleSiteChange = (siteId: string) => {
    setFormData({ ...formData, site_id: siteId });
    setErrors({ ...errors, site_id: '' });

    // Recheck username with new site
    if (formData.username && formData.username.length >= 3) {
      checkUsernameAvailability(formData.username, siteId || null);
    }

    const policyRoleName = normalizedRole === 'general_user' ? 'general_user' : 'site_admin';
    void loadSiteModuleAccess(siteId || null, formData.company_id || null, policyRoleName);
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};
    const role = (formData.role || '').toLowerCase();
    const requiresSite = ['site_admin', 'general_user'].includes(role);
    const requiresCompany = !['system_admin', 'super_admin'].includes(role);

    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    } else if (!usernameValidation.isValid && !usernameValidation.isChecking) {
      newErrors.username = usernameValidation.message || 'Username is not available';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!emailValidation.isValid) {
      newErrors.email = emailValidation.message || 'Invalid email format';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (!passwordStrength.isValid) {
      newErrors.password = 'Password does not meet strength requirements';
    }

    if (formData.password !== formData.confirm_password) {
      newErrors.confirm_password = 'Passwords do not match';
    }

    if (requiresSite && !formData.site_id) {
      newErrors.site_id = 'Site is required';
    }

    if (requiresCompany && !formData.company_id) {
      newErrors.company_id = 'Company selection is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    // Check if max_users limit is reached
    if (companySubscription.max_users !== null && formData.company_id) {
      try {
        // Fetch current user count for this company
        const usersResponse = await backendApi.getUsers();
        if (usersResponse.success && Array.isArray(usersResponse.users)) {
          const currentUserCount = usersResponse.users.filter(
            (u: any) => String(u.company_id) === String(formData.company_id)
          ).length;
          
          if (currentUserCount >= companySubscription.max_users) {
            setLimitMessage(
              `Maximum users limit (${companySubscription.max_users}) has been reached for this company. Please contact your system administrator to increase the limit.`
            );
            setShowLimitAlert(true);
            return;
          }
        }
      } catch (error) {
        console.error('Error checking user limit:', error);
      }
    }

    setLoading(true);
    setApiError(null);
    try {
      const normalizedRole = (formData.role || '').toLowerCase();
      const roleRequiresSite = ['site_admin', 'general_user'].includes(normalizedRole);
      const roleRequiresCompany = !['system_admin', 'super_admin'].includes(normalizedRole);

      const response = await backendApi.createUser({
        username: formData.username,
        email: formData.email,
        password: formData.password,
        first_name: formData.first_name,
        last_name: formData.last_name,
        role: formData.role,
        site_id: roleRequiresSite ? formData.site_id : null,
        is_active: formData.is_active,
        country_code: formData.country_code || null,
        phone_number: formData.phone_number || null,
        company_id: roleRequiresCompany ? (formData.company_id || null) : null
      });

      if (response.success) {
        const constrainedUserSelection = availableUserModules.reduce((acc, module) => {
          acc[module.module_key] = !!userModuleSelection[module.module_key];
          return acc;
        }, {} as Record<string, boolean>);

        if (normalizedRole === 'site_admin' || normalizedRole === 'ho_admin' || normalizedRole === 'company_admin') {
          // Role inherits scope defaults; explicit user overrides are not applied for admin roles.
          setApplyCustomUserModuleAccess(false);
        }

        if (isGeneralUserRole && applyCustomUserModuleAccess && response.user?.id) {
          await applyUserOverrides(backendApi, {
            userId: response.user.id,
            companyId: roleRequiresCompany ? (formData.company_id || null) : null,
            siteId: roleRequiresSite ? (formData.site_id || null) : null,
            moduleSelections: constrainedUserSelection,
          });
        }

        navigate('/admin/users');
      } else {
        const errorMsg = response.error || 'Failed to create user';
        const lower = errorMsg.toLowerCase();
        if (lower.includes('username')) {
          setErrors(prev => ({ ...prev, username: errorMsg }));
        } else if (lower.includes('email')) {
          setErrors(prev => ({ ...prev, email: errorMsg }));
        } else {
          setApiError(errorMsg);
        }
      }
    } catch (error: any) {
      setApiError(error.message || 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  const getStrengthColor = (strength: number) => {
    if (strength >= 80) return 'bg-green-500';
    if (strength >= 60) return 'bg-[#0084CF]';
    if (strength >= 40) return 'bg-yellow-500';
    if (strength >= 20) return 'bg-orange-500';
    return 'bg-red-500';
  };

  return (
    <div className="min-h-screen pt-20 lg:pt-4 pb-8 px-2 sm:px-3 lg:px-4">
      {showLimitAlert && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
        >
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-md mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-amber-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900">Limit Reached</h3>
            </div>
            <p className="text-sm text-gray-600 mb-6">{limitMessage}</p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowLimitAlert(false)}
                className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 font-medium hover:bg-gray-200 transition-colors"
              >
                Dismiss
              </button>
            </div>
          </div>
        </motion.div>
      )}
      <div className="max-w-7xl mx-auto space-y-6">
        <motion.section
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="admin-hero p-4 sm:p-4 lg:p-5"
        >
          <div className="admin-toolbar">
            <div className="space-y-2">
              <p className="admin-eyebrow">User onboarding</p>
              <div className="flex items-start gap-4">
                <div className="hidden sm:flex w-11 h-11 rounded-[16px] bg-[linear-gradient(135deg,#0084CF,#1C4D88)] items-center justify-center text-white shadow-[0_12px_20px_rgba(0,132,207,0.2)]">
                  <UserPlus size={22} />
                </div>
                <div>
                  <h1 className="admin-page-title">Create a new user account with role and module access in one flow.</h1>
                  <p className="admin-page-subtitle mt-2">The form is now arranged in wider, screen-fit sections so key fields are visible without excessive scrolling.</p>
                </div>
              </div>
            </div>

            <button
              onClick={() => navigate('/admin/users')}
              className="admin-button admin-button-ghost"
            >
              <ArrowLeft size={16} />
              Back to users
            </button>
          </div>
        </motion.section>

        {apiError && (
          <div className="flex items-center justify-between p-4 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800 rounded-xl text-red-700 dark:text-red-300 shadow-sm animate-in fade-in duration-200">
            <div className="flex items-center gap-3">
              <AlertCircle size={20} className="text-red-500 shrink-0" />
              <span className="text-sm font-medium">{apiError}</span>
            </div>
            <button
              type="button"
              onClick={() => setApiError(null)}
              className="text-red-500 hover:text-red-700 text-sm font-semibold ml-4"
            >
              Dismiss
            </button>
          </div>
        )}

        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="admin-section"
        >
          <div className="admin-section-header">
            <div>
              <h2 className="admin-section-title">User account form</h2>
              <p className="admin-section-copy">Complete identity, credentials, access role, and optional custom module permissions.</p>
            </div>
          </div>
          <div className="admin-section-body !p-4 sm:!p-5 lg:!p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
            {/* Company Selection */}
            <div className={`p-4 rounded-lg border mb-6 transition-all ${
              !roleRequiresCompany 
                ? 'bg-slate-50 border-slate-200 opacity-90' 
                : 'bg-[#F2F8FC]/50 border-[#E3F0F9]'
            }`}>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-semibold text-[#0D2747] flex items-center gap-2">
                  <Building2 size={18} className="text-[#0066A3]" />
                  Assigned Company {roleRequiresCompany && <span className="text-red-500">*</span>}
                </label>
                {!roleRequiresCompany && (
                  <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-blue-100 text-blue-800">
                    Not Required for System Admin
                  </span>
                )}
              </div>
              <div className="relative">
                <select
                  title="Assigned Company"
                  value={formData.company_id}
                  onChange={(e) => {
                    const selectedCompanyId = e.target.value;
                    setFormData({ ...formData, company_id: selectedCompanyId, site_id: '' });
                    setErrors({ ...errors, company_id: '' });
                    fetchSites(selectedCompanyId || null);
                    setSiteAllowedModuleKeys([]);
                    void loadCompanyModuleAccess(selectedCompanyId || null);
                  }}
                  disabled={(!isSystemLevelAdmin && !!user?.companyId) || !roleRequiresCompany}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#0084CF] outline-none transition-all appearance-none bg-white ${
                    errors.company_id ? 'border-red-500' : 'border-[#C2DFF1]'
                  } ${((!isSystemLevelAdmin && !!user?.companyId) || !roleRequiresCompany) ? 'bg-[#E3F0F9]/60 cursor-not-allowed text-gray-500' : ''}`}
                >
                  <option value="">{roleRequiresCompany ? 'Select a company' : 'Global Platform Access (All Companies)'}</option>
                  {companies.map((company) => (
                    <option key={company.company_id} value={company.company_id}>
                      {company.company_name} ({company.company_code})
                    </option>
                  ))}
                </select>
                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-[#0084CF]">
                  {loadingCompanies ? <Loader size={18} className="animate-spin" /> :
                    <Building2 size={18} className="rotate-0" />}
                </div>
              </div>
              {errors.company_id && (
                <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                  <AlertCircle size={14} />
                  {errors.company_id}
                </p>
              )}
              <p className="text-[#1C4D88]/70 text-xs mt-2">
                {!roleRequiresCompany
                  ? 'System Admin accounts have global access across all customer companies and do not belong to a single company.'
                  : 'Associate this user with a specific company and its dedicated database.'}
              </p>
            </div>

            {/* Username */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <UserIcon size={16} className="inline mr-2" />
                Username <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => handleUsernameChange(e.target.value)}
                  className={`w-full px-4 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-transparent bg-white text-gray-900 placeholder-gray-500 ${errors.username
                    ? 'border-red-500'
                    : usernameValidation.isValid && formData.username
                      ? 'border-green-500'
                      : usernameValidation.isChecking
                        ? 'border-[#0084CF]'
                        : 'border-gray-300'
                    }`}
                  style={{ color: '#111827', backgroundColor: '#ffffff' }}
                  placeholder="Enter username (min 3 characters)"
                  maxLength={30}
                />
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  {usernameValidation.isChecking && (
                    <Loader size={18} className="text-[#0084CF] animate-spin" />
                  )}
                  {!usernameValidation.isChecking && formData.username && (
                    usernameValidation.isValid ? (
                      <CheckCircle size={18} className="text-green-500" />
                    ) : usernameValidation.message ? (
                      <XCircle size={18} className="text-red-500" />
                    ) : null
                  )}
                </div>
              </div>
              <div className="mt-1">
                {errors.username && (
                  <p className="text-red-500 text-sm flex items-center gap-1">
                    <AlertCircle size={14} />
                    {errors.username}
                  </p>
                )}
                {!errors.username && usernameValidation.message && (
                  <p className={`text-sm flex items-center gap-1 ${usernameValidation.isValid ? 'text-green-600' : 'text-red-500'
                    }`}>
                    {usernameValidation.isValid ? (
                      <CheckCircle size={14} />
                    ) : (
                      <XCircle size={14} />
                    )}
                    {usernameValidation.message}
                  </p>
                )}
                {formData.username && (
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.username.length}/30 characters
                  </p>
                )}
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Mail size={16} className="inline mr-2" />
                Email <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleEmailChange(e.target.value)}
                  style={{ WebkitTextFillColor: '#111827' }}
                  className={`w-full px-4 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-transparent bg-white text-gray-900 placeholder-gray-500 [&:-webkit-autofill]:[-webkit-text-fill-color:#111827] ${errors.email
                    ? 'border-red-500'
                    : emailValidation.isValid && formData.email
                      ? 'border-green-500'
                      : emailValidation.isChecking
                        ? 'border-[#0084CF]'
                        : 'border-gray-300'
                    }`}
                  placeholder="user@example.com"
                />
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  {emailValidation.isChecking && (
                    <Loader size={18} className="text-[#0084CF] animate-spin" />
                  )}
                  {!emailValidation.isChecking && formData.email && emailValidation.isValid && (
                    <CheckCircle size={18} className="text-green-500" />
                  )}
                  {!emailValidation.isChecking && formData.email && !emailValidation.isValid && emailValidation.message && (
                    <XCircle size={18} className="text-red-500" />
                  )}
                </div>
              </div>
              {errors.email && (
                <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                  <AlertCircle size={14} />
                  {errors.email}
                </p>
              )}
              {!errors.email && emailValidation.message && (
                <p className={`text-sm mt-1 flex items-center gap-1 ${emailValidation.isValid ? 'text-green-600' : 'text-red-500'
                  }`}>
                  {emailValidation.isValid ? (
                    <CheckCircle size={14} />
                  ) : (
                    <XCircle size={14} />
                  )}
                  {emailValidation.message}
                </p>
              )}
            </div>

            {/* Name */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  First Name
                </label>
                <input
                  type="text"
                  value={formData.first_name}
                  onChange={(e) => {
                    setFormData({ ...formData, first_name: e.target.value });
                    setErrors({ ...errors, first_name: '' });
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-transparent bg-white text-gray-900 placeholder-gray-500"
                  placeholder="John"
                  maxLength={50}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Last Name
                </label>
                <input
                  type="text"
                  value={formData.last_name}
                  onChange={(e) => {
                    setFormData({ ...formData, last_name: e.target.value });
                    setErrors({ ...errors, last_name: '' });
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-transparent bg-white text-gray-900 placeholder-gray-500"
                  placeholder="Doe"
                  maxLength={50}
                />
              </div>
            </div>

            {/* Country Code and Phone Number */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Country Code
                </label>
                <input
                  type="text"
                  value={formData.country_code}
                  onChange={(e) => {
                    setFormData({ ...formData, country_code: e.target.value });
                    setErrors({ ...errors, country_code: '' });
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-transparent bg-white text-gray-900 placeholder-gray-500"
                  placeholder="+91"
                  maxLength={10}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={formData.phone_number}
                  onChange={(e) => {
                    setFormData({ ...formData, phone_number: e.target.value });
                    setErrors({ ...errors, phone_number: '' });
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-transparent bg-white text-gray-900 placeholder-gray-500"
                  placeholder="1234567890"
                  maxLength={20}
                />
              </div>
            </div>

            {/* Password */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <LockIcon size={16} className="inline mr-2" />
                  Password <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={(e) => handlePasswordChange(e.target.value)}
                    className={`w-full px-4 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-transparent bg-white text-gray-900 placeholder-gray-500 ${errors.password
                      ? 'border-red-500'
                      : passwordStrength.isValid && formData.password
                        ? 'border-green-500'
                        : 'border-gray-300'
                      }`}
                    placeholder="Create a strong password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>

                {/* Password Strength Meter */}
                {formData.password && (
                  <div className="mt-2">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className={`h-full transition-all duration-300 ${getStrengthColor(passwordStrength.strength)}`}
                          style={{ width: `${passwordStrength.strength}%` }}
                        />
                      </div>
                      <span className={`text-xs font-medium ${passwordStrength.strength >= 80 ? 'text-green-600' :
                        passwordStrength.strength >= 60 ? 'text-[#0066A3]' :
                          passwordStrength.strength >= 40 ? 'text-yellow-600' :
                            'text-red-600'
                        }`}>
                        {passwordStrength.label}
                      </span>
                    </div>
                    <div className="text-xs text-gray-600 space-y-1">
                      {passwordStrength.feedback.map((item, idx) => (
                        <div key={idx} className={`flex items-center gap-1 ${passwordStrength.feedback.length === 1 && passwordStrength.feedback[0] === 'Strong password!'
                          ? 'text-green-600'
                          : 'text-gray-600'
                          }`}>
                          {passwordStrength.feedback.length === 1 && passwordStrength.feedback[0] === 'Strong password!' ? (
                            <CheckCircle size={12} />
                          ) : (
                            <XCircle size={12} />
                          )}
                          {item}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {errors.password && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                    <AlertCircle size={14} />
                    {errors.password}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Confirm Password <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    value={formData.confirm_password}
                    onChange={(e) => handleConfirmPasswordChange(e.target.value)}
                    className={`w-full px-4 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-transparent bg-white text-gray-900 placeholder-gray-500 ${errors.confirm_password
                      ? 'border-red-500'
                      : formData.confirm_password && formData.password === formData.confirm_password
                        ? 'border-green-500'
                        : 'border-gray-300'
                      }`}
                    placeholder="Confirm password"
                  />
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center gap-2">
                    {formData.confirm_password && formData.password === formData.confirm_password && (
                      <CheckCircle size={18} className="text-green-500" />
                    )}
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
                {errors.confirm_password && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                    <AlertCircle size={14} />
                    {errors.confirm_password}
                  </p>
                )}
                {formData.confirm_password && formData.password === formData.confirm_password && !errors.confirm_password && (
                  <p className="text-green-600 text-sm mt-1 flex items-center gap-1">
                    <CheckCircle size={14} />
                    Passwords match
                  </p>
                )}
              </div>
            </div>

            {/* Role, Company, and Site Configuration */}
            <div className="space-y-4 pt-4 border-t border-gray-100">
              <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <Shield size={16} className="text-[#0066A3]" />
                Access & Permissions
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Role Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Role <span className="text-red-500">*</span>
                  </label>
                  <select
                    title="Select User Role"
                    value={formData.role}
                    onChange={(e) => {
                      const newRole = e.target.value;
                      const isSys = ['system_admin', 'super_admin'].includes(newRole);
                      setFormData(prev => ({
                        ...prev,
                        role: newRole,
                        company_id: isSys ? '' : prev.company_id,
                        // Reset site if switching to a role that doesn't use it
                        site_id: ['ho_admin', 'company_admin', 'system_admin', 'super_admin'].includes(newRole) ? '' : prev.site_id
                      }));

                      if (isSys) {
                        setErrors(prev => {
                          const next = { ...prev };
                          delete next.company_id;
                          delete next.site_id;
                          return next;
                        });
                      }

                      if (['ho_admin', 'company_admin', 'site_admin', 'system_admin'].includes(newRole)) {
                        setApplyCustomUserModuleAccess(false);
                      }

                      if (['site_admin', 'general_user', 'user', 'admin'].includes(newRole) && formData.site_id && formData.company_id) {
                        const roleForPolicy = newRole === 'general_user' ? 'general_user' : 'site_admin';
                        void loadSiteModuleAccess(formData.site_id, formData.company_id, roleForPolicy);
                      }
                    }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-transparent text-gray-900 bg-white"
                  >
                    {roles.length > 0 ? (
                      roles.map(role => (
                        <option key={role.role_id} value={role.role_name}>
                          {role.display_name}
                        </option>
                      ))
                    ) : (
                      <>
                        <option value="general_user">General User</option>
                        <option value="site_admin">Site Admin</option>
                      </>
                    )}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.role === 'system_admin' && 'Full access to all companies and sites.'}
                    {(formData.role === 'ho_admin' || formData.role === 'company_admin') && 'Manages a specific Company/Customer (Head Office).'}
                    {formData.role === 'site_admin' && 'Manages a specific Site.'}
                    {formData.role === 'general_user' && 'Regular user with access to assigned site data.'}
                  </p>
                </div>

                {/* Site Selection - Only for Site Admin & General User */}
                {['site_admin', 'general_user', 'user', 'admin'].includes(formData.role) && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Building2 size={16} className="inline mr-2" />
                      Site <span className="text-red-500">*</span>
                    </label>
                    <select
                      title="Select Site"
                      value={formData.site_id}
                      onChange={(e) => handleSiteChange(e.target.value)}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-transparent text-gray-900 bg-white ${errors.site_id ? 'border-red-500' : 'border-gray-300'
                        }`}
                    >
                      <option value="">Select a site</option>
                      {filteredSites.map((site) => (
                        <option key={site.site_id} value={site.site_id}>
                          {site.site_name} ({site.site_code})
                        </option>
                      ))}
                    </select>

                    {errors.site_id && (
                      <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                        <AlertCircle size={14} />
                        {errors.site_id}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-3 border border-[#E3F0F9] rounded-lg p-4 bg-[#F2F8FC]/40">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h3 className="text-xl font-black text-black">Custom Module Access</h3>
                  <p className="text-base font-extrabold text-gray-900">
                    {isGeneralUserRole
                      ? 'General User can get a subset of site-assigned modules.'
                      : 'Admin roles inherit scope defaults and cannot override here.'}
                  </p>
                </div>
                <label className="flex items-center gap-2 text-lg font-black text-black">
                  <input
                    type="checkbox"
                    checked={applyCustomUserModuleAccess}
                    disabled={!isGeneralUserRole}
                    onChange={(e) => setApplyCustomUserModuleAccess(e.target.checked)}
                  />
                  Apply custom access
                </label>
              </div>

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => {
                    const allEnabled: Record<string, boolean> = {};
                    availableUserModules.forEach((module) => {
                      allEnabled[module.module_key] = true;
                    });
                    setUserModuleSelection((prev) => ({ ...prev, ...allEnabled }));
                  }}
                  className="px-3 py-1.5 text-sm font-black border-2 border-[#0066A3] text-[#1C4D88] rounded-lg hover:bg-[#F2F8FC]"
                >
                  Enable All
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const allDisabled: Record<string, boolean> = {};
                    availableUserModules.forEach((module) => {
                      allDisabled[module.module_key] = false;
                    });
                    setUserModuleSelection((prev) => ({ ...prev, ...allDisabled }));
                  }}
                  className="px-3 py-1.5 text-sm font-black border-2 border-red-600 text-red-700 rounded-lg hover:bg-red-50"
                >
                  Disable All
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {availableUserModules.map((module) => (
                  <label key={module.module_key} className="flex items-center justify-between gap-2 border border-gray-200 rounded-md px-3 py-2 bg-white">
                    <span className="text-base font-black text-black">{module.module_name}</span>
                    <input
                      type="checkbox"
                      checked={!!userModuleSelection[module.module_key]}
                      onChange={(e) => {
                        setUserModuleSelection((prev) => ({
                          ...prev,
                          [module.module_key]: e.target.checked,
                        }));
                      }}
                    />
                  </label>
                ))}
              </div>
              {availableUserModules.length === 0 && (
                <p className="text-base font-black text-amber-950 mt-2">No modules available from selected company/site scope.</p>
              )}
            </div>

            {/* Active Status */}
            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="w-4 h-4 text-[#0084CF] border-gray-300 rounded focus:ring-[#0084CF]"
                />
                <span className="text-sm font-medium text-gray-700">User is active</span>
              </label>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3 pt-5 border-t border-gray-200">
              <button
                type="button"
                onClick={() => navigate('/admin/users')}
                className="admin-button admin-button-ghost"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || usernameValidation.isChecking || emailValidation.isChecking}
                className="admin-button admin-button-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save size={20} />
                {loading ? 'Creating...' : 'Create User'}
              </button>
            </div>
          </form>
          </div>
        </motion.section>
      </div>
    </div>
  );
};

export default UserCreate;
